import type { ReactNode } from 'react'

import { Pagination } from 'antd'
import cx from 'classnames'

import './page-grid.css'

const numberFormatter = new Intl.NumberFormat('en-US')

export interface PageGridPaginationSummaryInfo {
  start: number
  end: number
  total: number
  overallTotal?: number
  itemsLabel: string
}

export interface PageGridPaginationProps {
  current: number
  pageSize: number
  total: number
  overallTotal?: number
  onChange?: (page: number, pageSize: number) => void
  className?: string
  disabled?: boolean
  pageSizeOptions?: Array<number | string>
  itemsLabel?: string
  summary?: ReactNode | ((info: PageGridPaginationSummaryInfo) => ReactNode)
  hideOnSinglePage?: boolean
}

function formatNumber(value: number): string {
  return numberFormatter.format(value)
}

export function PageGridPagination({
  current,
  pageSize,
  total,
  overallTotal,
  onChange,
  className,
  disabled = false,
  pageSizeOptions = [10, 25, 50, 100],
  itemsLabel = 'items',
  summary,
  hideOnSinglePage = false
}: PageGridPaginationProps): JSX.Element {
  const safeCurrent = current > 0 ? current : 1
  const safePageSize = pageSize > 0 ? pageSize : 10

  const hasItems = total > 0
  const start = hasItems ? (safeCurrent - 1) * safePageSize + 1 : 0
  const end = hasItems ? Math.min(safeCurrent * safePageSize, total) : 0

  const summaryInfo: PageGridPaginationSummaryInfo = {
    start,
    end,
    total,
    overallTotal,
    itemsLabel
  }

  const computedSummary =
    typeof summary === 'function'
      ? summary(summaryInfo)
      : (summary ?? (
          <span className='page-grid__summary'>
            {hasItems ? (
              <>
                Showing {formatNumber(start)}–{formatNumber(end)} of {formatNumber(total)}{' '}
                {itemsLabel}
                {overallTotal !== undefined && overallTotal !== total
                  ? ` • Total ${formatNumber(overallTotal)} ${itemsLabel}`
                  : ''}
              </>
            ) : (
              <>Showing 0 of 0 {itemsLabel}</>
            )}
          </span>
        ))

  return (
    <div className={cx('page-grid__pagination border-t border-slate-200 pt-4', className)}>
      {computedSummary}
      <Pagination
        current={safeCurrent}
        pageSize={safePageSize}
        total={total}
        disabled={disabled}
        onChange={onChange}
        showSizeChanger
        showLessItems
        pageSizeOptions={pageSizeOptions.map(option => option.toString())}
        hideOnSinglePage={hideOnSinglePage}
      />
    </div>
  )
}
